import { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../../api";
import type { Manifest, MappingRule, MatchOn, RuleAction, RuleRunResult } from "../../api/types";
import { ErrorBox, Modal, Spinner } from "../../components/bits";
import { Icon } from "../../components/icons";
import { connectorLabel } from "../../lib/format";
import { loadMembers, toast, toastError } from "../../store/app";
import { PeoplePage } from "../settings/PeoplePage";
import { ConfirmDialog, useOrgData } from "./shared";

const MATCH_LABELS: Record<MatchOn, string> = { email: "Email", username: "Username", attribute: "Attribute" };
const ACTION_LABELS: Record<RuleAction, string> = {
  link: "Link to matching member",
  create_ghost: "Create ghost person",
  ignore: "Ignore",
};

type Draft = Omit<MappingRule, "id">;

function DomainChips({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState("");
  const add = () => {
    const parts = text
      .split(/[,\s]+/)
      .map((x) => x.replace(/^@/, "").trim().toLowerCase())
      .filter(Boolean);
    if (parts.length) onChange([...new Set([...value, ...parts])]);
    setText("");
  };
  return (
    <div className="chips-field">
      {value.map((d) => (
        <span key={d} className="chip removable">
          @{d}
          <button type="button" aria-label={`Remove ${d}`} onClick={() => onChange(value.filter((x) => x !== d))}>
            ×
          </button>
        </span>
      ))}
      <input
        aria-label="Add alias domain"
        placeholder="example.org"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => text && add()}
      />
    </div>
  );
}

function RuleForm({
  initial,
  connectors,
  onCancel,
  onSave,
}: {
  initial: Draft;
  connectors: Manifest[];
  onCancel: () => void;
  onSave: (d: Draft) => Promise<void>;
}) {
  const [d, setD] = useState<Draft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const setT = (p: Partial<Draft["transform"]>) => setD((x) => ({ ...x, transform: { ...x.transform, ...p } }));
  return (
    <form
      className="form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await onSave({ ...d, attribute: d.match_on === "attribute" ? d.attribute?.trim() || null : null });
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="field-row">
        <label className="field">
          <span>Applies to</span>
          <select value={d.applies_to} onChange={(e) => set({ applies_to: e.target.value })}>
            <option value="*">All connectors</option>
            {connectors.map((c) => (
              <option key={c.connector_type} value={c.connector_type}>
                {(c.display_name as string | undefined) ?? connectorLabel(c.connector_type)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Match on</span>
          <select value={d.match_on} onChange={(e) => set({ match_on: e.target.value as MatchOn })}>
            {(Object.keys(MATCH_LABELS) as MatchOn[]).map((m) => (
              <option key={m} value={m}>
                {MATCH_LABELS[m]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {d.match_on === "attribute" && (
        <label className="field">
          <span>Attribute</span>
          <input
            required
            value={d.attribute ?? ""}
            onChange={(e) => set({ attribute: e.target.value })}
            placeholder="e.g. employee_id"
          />
        </label>
      )}
      <fieldset className="field">
        <legend>Transform</legend>
        <label className="check-row">
          <input type="checkbox" checked={!!d.transform.lowercase} onChange={(e) => setT({ lowercase: e.target.checked })} />
          Lowercase before comparing
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={!!d.transform.strip_domain}
            onChange={(e) => setT({ strip_domain: e.target.checked })}
          />
          Strip the email domain (compare only the part before @)
        </label>
        <span className="small muted">Alias domains (treated as equal to the org domain)</span>
        <DomainChips value={d.transform.alias_domains ?? []} onChange={(alias_domains) => setT({ alias_domains })} />
      </fieldset>
      <label className="field">
        <span>Action</span>
        <select value={d.action} onChange={(e) => set({ action: e.target.value as RuleAction })}>
          {(Object.keys(ACTION_LABELS) as RuleAction[]).map((a) => (
            <option key={a} value={a}>
              {ACTION_LABELS[a]}
            </option>
          ))}
        </select>
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Saving…" : "Save rule"}
        </button>
      </div>
    </form>
  );
}

function describeRule(r: MappingRule): string {
  const parts: string[] = [];
  parts.push(`${r.applies_to === "*" ? "Any connector" : connectorLabel(r.applies_to)}: match on ${r.match_on === "attribute" ? `attribute “${r.attribute ?? "?"}”` : r.match_on}`);
  const t: string[] = [];
  if (r.transform.lowercase) t.push("lowercase");
  if (r.transform.strip_domain) t.push("strip domain");
  if (r.transform.alias_domains?.length) t.push(`aliases ${r.transform.alias_domains.map((d) => "@" + d).join(", ")}`);
  if (t.length) parts.push(`(${t.join(", ")})`);
  return parts.join(" ");
}

function MappingRules() {
  const { orgId, data, setData, error, forbidden, reload } = useOrgData((id) => api.admin.rules(id));
  const [connectors, setConnectors] = useState<Manifest[]>([]);
  const [editing, setEditing] = useState<MappingRule | "new" | null>(null);
  const [deleting, setDeleting] = useState<MappingRule | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RuleRunResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .connectors()
      .then((r) => setConnectors(r.connectors))
      .catch(() => setConnectors([]));
  }, []);

  const rules = useMemo(() => [...(data?.rules ?? [])].sort((a, b) => a.priority - b.priority), [data]);
  if (forbidden) return null;

  const move = async (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= rules.length) return;
    const order = [...rules];
    [order[index], order[j]] = [order[j], order[index]];
    // Renumber in steps of 10 and patch rules whose priority changed.
    const next = order.map((r, i) => ({ ...r, priority: (i + 1) * 10 }));
    setData({ rules: next });
    setBusy(true);
    try {
      await Promise.all(
        next.filter((r) => rules.find((o) => o.id === r.id)?.priority !== r.priority).map((r) => api.admin.patchRule(r.id, { priority: r.priority })),
      );
    } catch (e) {
      toastError(e, "Reorder failed");
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="rules-title" className="admin-section">
      <div className="section-head">
        <h2 id="rules-title">Mapping rules</h2>
        <div className="section-actions">
          <button
            type="button"
            className="btn"
            disabled={running || !orgId}
            onClick={async () => {
              if (!orgId) return;
              setRunning(true);
              try {
                setResult(await api.admin.runRules(orgId));
                void loadMembers(true).catch(() => undefined);
              } catch (e) {
                toastError(e, "Running rules failed");
              } finally {
                setRunning(false);
              }
            }}
          >
            <Icon name="refresh" size={14} />
            {running ? "Running…" : "Run rules now"}
          </button>
          <button type="button" className="btn primary" onClick={() => setEditing("new")}>
            <Icon name="plus" size={14} />
            Add rule
          </button>
        </div>
      </div>
      <p className="muted small">
        Rules link identities reported by connected tools to people, in priority order (first match wins).
      </p>
      {result && (
        <div className="result-box" role="status">
          <span>
            Linked <strong>{result.linked}</strong> · ghosts created <strong>{result.ghosts_created}</strong> · still
            unmatched <strong>{result.unmatched}</strong>
          </span>
          <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => setResult(null)}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!data && !error ? (
        <Spinner />
      ) : rules.length === 0 ? (
        <p className="muted small">No rules yet. Without rules, identities are matched by exact email only.</p>
      ) : (
        <ol className="rule-list">
          {rules.map((r, i) => (
            <li key={r.id} className="rule">
              <span className="rule-prio" title="Priority">
                {i + 1}
              </span>
              <div className="rule-main">
                <div>{describeRule(r)}</div>
                <div className="muted small">→ {ACTION_LABELS[r.action]}</div>
              </div>
              <div className="row-actions">
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Move rule ${i + 1} up`}
                  disabled={i === 0 || busy}
                  onClick={() => void move(i, -1)}
                >
                  <Icon name="chevronUp" />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Move rule ${i + 1} down`}
                  disabled={i === rules.length - 1 || busy}
                  onClick={() => void move(i, 1)}
                >
                  <Icon name="chevronDown" />
                </button>
                <button type="button" className="btn small" onClick={() => setEditing(r)}>
                  Edit
                </button>
                <button type="button" className="btn small danger" onClick={() => setDeleting(r)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {editing && orgId && (
        <Modal title={editing === "new" ? "Add mapping rule" : "Edit mapping rule"} onClose={() => setEditing(null)}>
          <RuleForm
            connectors={connectors}
            initial={
              editing === "new"
                ? {
                    priority: (rules[rules.length - 1]?.priority ?? 0) + 10,
                    applies_to: "*",
                    match_on: "email",
                    attribute: null,
                    transform: { lowercase: true },
                    action: "link",
                  }
                : (({ id: _id, ...rest }) => rest)(editing)
            }
            onCancel={() => setEditing(null)}
            onSave={async (draft) => {
              if (editing === "new") {
                const created = await api.admin.createRule(orgId, draft);
                setData((d) => ({ rules: [...(d?.rules ?? []), created] }));
              } else {
                const updated = await api.admin.patchRule(editing.id, draft);
                setData((d) => ({ rules: (d?.rules ?? []).map((x) => (x.id === updated.id ? updated : x)) }));
              }
              toast("Rule saved", "success");
              setEditing(null);
            }}
          />
        </Modal>
      )}
      {deleting && (
        <ConfirmDialog
          title="Delete rule?"
          confirmLabel="Delete"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              await api.admin.deleteRule(deleting.id);
              setData((d) => ({ rules: (d?.rules ?? []).filter((x) => x.id !== deleting.id) }));
              setDeleting(null);
            } catch (e) {
              toastError(e, "Delete failed");
            }
          }}
        >
          <p>{describeRule(deleting)}</p>
          <p className="muted small">Existing links stay; the rule just no longer applies to new identities.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

export function IdentitiesTab() {
  return (
    <div className="admin-identities">
      <MappingRules />
      <div className="admin-section">
        <PeoplePage />
      </div>
    </div>
  );
}
